// ================================================================
// 大師登入頁面
// ================================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, AutoAwesome } from '@mui/icons-material';
import { useTeacher } from '../../contexts/TeacherContext';

const TeacherLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isTeacherAuthenticated } = useTeacher();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isTeacherAuthenticated) {
      const from =
        (location.state as any)?.from?.pathname || '/teacher/dashboard';
      navigate(from, { replace: true });
    }
  }, [isTeacherAuthenticated, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await login({ username, password });
      const from =
        (location.state as any)?.from?.pathname || '/teacher/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || '登入失敗，請檢查帳號密碼');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d1b69 0%, #7b2d8b 100%)',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <AutoAwesome sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" color="secondary">
              大師登入
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              好運紫微斗數 大師諮詢系統
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="帳號"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoComplete="username"
              autoFocus
            />

            <TextField
              fullWidth
              label="密碼"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="secondary"
              size="large"
              disabled={isLoading || !username || !password}
              sx={{ mt: 3, py: 1.5 }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : '登入'}
            </Button>
          </form>

          <Box textAlign="center" mt={3}>
            <Button
              variant="text"
              onClick={() => navigate('/')}
              sx={{ textTransform: 'none' }}
            >
              返回首頁
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeacherLoginPage;
